import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { UNV } from '../../../enums/address-verification-status';
import { AddressValidationStatus } from '../../../components/address-validation/validation-status';
import { ValidateAddressBox } from '../../../components/validate-address-box';
import { FIELD_NAMES } from '../../../address/address-fields/constants';
import { useShipFromContext } from '../../contexts/ship-from-context';
import { messages } from '../messages';

import type { ShipFromContextValue } from '../../contexts/ship-from-context-types';

type ShipFromAddress = Record<string, unknown>;

const normalizeAddressField = (val: unknown): string => (typeof val === 'string' ? val : '').toLowerCase().trim();

const addressesMatch = (a1: ShipFromAddress, a2: ShipFromAddress): boolean =>
  normalizeAddressField(a1.line1) === normalizeAddressField(a2.line1) &&
  normalizeAddressField(a1.city) === normalizeAddressField(a2.city) &&
  normalizeAddressField(a1.state) === normalizeAddressField(a2.state) &&
  normalizeAddressField(a1.postalCode) === normalizeAddressField(a2.postalCode) &&
  normalizeAddressField(a1.countryCode) === normalizeAddressField(a2.countryCode);

const ADDRESS_GEOGRAPHY_FIELDS = [
  FIELD_NAMES.LINE1,
  FIELD_NAMES.LINE2,
  FIELD_NAMES.LINE3,
  FIELD_NAMES.CITY,
  FIELD_NAMES.STATE,
  FIELD_NAMES.POSTAL_CODE,
  FIELD_NAMES.COUNTRY_CODE,
] as const;

type AddressLike = Record<string, unknown>;

const pickAddressGeographyFields = (matchedAddress: AddressLike): AddressLike =>
  ADDRESS_GEOGRAPHY_FIELDS.reduce<AddressLike>(
    (acc, field) => (field in matchedAddress ? { ...acc, [field]: matchedAddress[field] } : acc),
    {},
  );

const REQUIRED_FIELDS_FOR_VALIDATION = [FIELD_NAMES.LINE1, FIELD_NAMES.CITY, FIELD_NAMES.STATE, FIELD_NAMES.POSTAL_CODE];

const hasRequiredValidationErrors = (originErrors: Record<string, unknown> | undefined): boolean => {
  if (!originErrors) {
    return false;
  }
  return REQUIRED_FIELDS_FOR_VALIDATION.some((field) => !!originErrors[field]);
};

export const OriginAddressValidation = () => {
  const {
    shipFrom,
    updateShipFrom,
    validationErrors,
    isShipFromAddressValidationEnabled,
    validationStatusCode,
    validationMessage,
    suggestedAddress,
    isValidating,
    validateOriginAddress,
    returnAddressSameAsShipFrom,
  } = useShipFromContext() as unknown as ShipFromContextValue;

  const onValidateClick = useCallback(() => {
    validateOriginAddress(shipFrom.originAddress);
  }, [validateOriginAddress, shipFrom.originAddress]);

  const onUseValidatedAddress = useCallback(() => {
    if (!suggestedAddress) {
      return;
    }
    const mergedAddress = {
      ...shipFrom.originAddress,
      ...pickAddressGeographyFields(suggestedAddress as AddressLike),
    };
    const updatePayload: Record<string, unknown> = { originAddress: mergedAddress };
    if (returnAddressSameAsShipFrom) {
      updatePayload.returnAddress = mergedAddress;
    }
    updateShipFrom(updatePayload);
  }, [suggestedAddress, shipFrom.originAddress, returnAddressSameAsShipFrom, updateShipFrom]);

  if (!isShipFromAddressValidationEnabled) {
    return null;
  }

  const shouldHideValidateButton =
    isValidating || hasRequiredValidationErrors(validationErrors?.originAddress as Record<string, unknown> | undefined);
  const shouldShowSuggestion =
    !!suggestedAddress && validationStatusCode !== UNV && !addressesMatch(suggestedAddress, shipFrom.originAddress);

  return (
    <div>
      {isValidating ? (
        <span>
          <FormattedMessage {...messages.validatingAddress} />
        </span>
      ) : (
        <AddressValidationStatus
          countryCode={shipFrom.originAddress.countryCode as string}
          hideValidateButton={shouldHideValidateButton}
          residentialIndicator={shipFrom.originAddress.residentialIndicator as string}
          shouldTruncateValidationIssue={false}
          validateAddress={onValidateClick}
          validation={validationStatusCode}
          validationMessage={validationMessage}
        />
      )}
      {shouldShowSuggestion && <ValidateAddressBox onUseValidatedAddress={onUseValidatedAddress} validatedAddress={suggestedAddress} />}
    </div>
  );
};
